/**
 * Layout sharing — encode/decode the structural parts of the tracker state
 * (counts + always selection + starting items) into a short string a user
 * can share.
 *
 * The string carries STRUCTURE + STARTING INVENTORY only (counts, always
 * names, isWoth flag, starting items). It does NOT carry filled hint data
 * (locations, picked items) — only the empty layout, so a friend can start
 * from the same blank tracker.
 *
 * v2: structure only.
 * v3: adds startingItems (id + optional upgrade state).
 */

const VERSION = 3;
const PREFIX = 'zr-layout-v3.';
const LEGACY_PREFIX_V2 = 'zr-layout-v2.';

export type SometimesKindCode = 'regular' | 'dual' | 'entrance';

export interface StartingItem {
  id: string;
  /** Upgrade level for upgrade-type items (>= 1 means obtained). Omit/0 = simple obtained. */
  state?: number;
}

export interface SharedLayout {
  v: number;
  nbGoals: number;
  nbSometimes: number;
  nbBarren: number;
  isWoth: boolean;
  alwaysHintNames: string[];
  /** Kind for each sometimes slot, length === nbSometimes. */
  sometimesKinds: SometimesKindCode[];
  /** Items pre-marked as obtained when the layout is applied. */
  startingItems?: StartingItem[];
}

export type SharedLayoutPayload = Omit<SharedLayout, 'v'>;

function toBase64Url(s: string): string {
  return btoa(unescape(encodeURIComponent(s)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function fromBase64Url(s: string): string {
  const padded = s.replace(/-/g, '+').replace(/_/g, '/');
  return decodeURIComponent(escape(atob(padded)));
}

export function encodeLayout(payload: SharedLayoutPayload): string {
  const obj: SharedLayout = { v: VERSION, ...payload };
  return PREFIX + toBase64Url(JSON.stringify(obj));
}

export function decodeLayout(input: string): SharedLayoutPayload | null {
  const trimmed = input.trim();
  const prefix = trimmed.startsWith(PREFIX)
    ? PREFIX
    : trimmed.startsWith(LEGACY_PREFIX_V2)
      ? LEGACY_PREFIX_V2
      : null;
  if (!prefix) return null;

  try {
    const json = fromBase64Url(trimmed.slice(prefix.length));
    const parsed = JSON.parse(json);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      (parsed.v !== 2 && parsed.v !== 3) ||
      typeof parsed.nbGoals !== 'number' ||
      typeof parsed.nbSometimes !== 'number' ||
      typeof parsed.nbBarren !== 'number' ||
      typeof parsed.isWoth !== 'boolean' ||
      !Array.isArray(parsed.alwaysHintNames) ||
      !parsed.alwaysHintNames.every((n: unknown) => typeof n === 'string') ||
      !Array.isArray(parsed.sometimesKinds) ||
      !parsed.sometimesKinds.every(
        (k: unknown) => k === 'regular' || k === 'dual' || k === 'entrance'
      )
    ) {
      return null;
    }

    let startingItems: StartingItem[] | undefined;
    if (parsed.v === 3 && Array.isArray(parsed.startingItems)) {
      const cleaned: StartingItem[] = [];
      for (const it of parsed.startingItems) {
        if (it && typeof it.id === 'string') {
          const entry: StartingItem = { id: it.id };
          if (typeof it.state === 'number') entry.state = it.state;
          cleaned.push(entry);
        }
      }
      startingItems = cleaned;
    }

    const { nbGoals, nbSometimes, nbBarren, isWoth, alwaysHintNames, sometimesKinds } = parsed;
    return {
      nbGoals,
      nbSometimes,
      nbBarren,
      isWoth,
      alwaysHintNames,
      sometimesKinds,
      ...(startingItems ? { startingItems } : {}),
    };
  } catch {
    return null;
  }
}
